/* eslint-disable @angular-eslint/directive-selector */
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { FormGroupDirective } from '@angular/forms';



@Directive({ selector: '[phoneFormatter]' })
export class PhoneFormatterDirective implements AfterViewInit, OnDestroy {
  @Input('phoneFormatter') countryCode: string = 'US';
  @Input() controlName?: string;

  private readonly DIGIT_REGEX = /\d/;
  private inputElement?: HTMLInputElement;
  private removeInputListener?: () => void;
  private lastFormattedValue = '';

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly form: FormGroupDirective,
  ) { }

  ngAfterViewInit(): void {
    this.inputElement =
      this.el.nativeElement.querySelector('input') ?? this.el.nativeElement;

    if (this.inputElement) {
      this.removeInputListener = this.renderer.listen(
        this.inputElement,
        'input',
        () => this.onInput(),
      );
    }
  }

  ngOnDestroy(): void {
    this.removeInputListener?.();
  }

  private onInput(): void {
    if (!this.inputElement || !this.countryCode) {
      return;
    }

    const currentValue = this.inputElement.value;
    if (!currentValue) {
      this.lastFormattedValue = '';
      this.updateFormControl('');
      return;
    }

    const formattedValue = new AsYouType(this.countryCode as CountryCode).input(currentValue);
    if (formattedValue === this.lastFormattedValue) {
      return;
    }

    const cursorPosition = this.calculateCursorPosition(
      currentValue,
      formattedValue,
      this.inputElement.selectionStart ?? 0,
    );

    this.updateInputValue(formattedValue, cursorPosition);
    this.updateFormControl(formattedValue);
  }

  private calculateCursorPosition(
    oldValue: string,
    newValue: string,
    oldPosition: number,
  ): number {
    const digitsBeforeCursor = (oldValue.substring(0, oldPosition).match(/\d/g) ?? []).length;
    if (digitsBeforeCursor <= 0) {
      return 0;
    }

    let digitCount = 0;
    for (let i = 0; i < newValue.length; i++) {
      if (this.DIGIT_REGEX.test(newValue[i])) {
        digitCount++;
        if (digitCount >= digitsBeforeCursor) {
          return i + 1;
        }
      }
    }

    return newValue.length;
  }

  private updateInputValue(value: string, cursorPosition: number): void {
    if (!this.inputElement) {
      return;
    }

    this.inputElement.value = value;
    this.lastFormattedValue = value;

    const safePosition = Math.max(
      0,
      Math.min(cursorPosition, value.length),
    );
    this.inputElement.setSelectionRange(safePosition, safePosition);
  }

  private updateFormControl(value: string): void {
    if (!this.controlName) {
      return;
    }

    const control = this.form.control.get(this.controlName);
    if (control?.value !== value) {
      control?.setValue(value, { emitEvent: true });
    }
  }
}
